import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  PersonAdd,
  MoreVert,
  Edit,
  PersonOff,
  PersonOutline,
  Email,
  Search,
  AdminPanelSettings,
  Person,
  FilterList as FilterIcon,
  FilterListOff as FilterOffIcon
} from '@mui/icons-material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridColumnMenu
} from '@mui/x-data-grid';
import { styled, useTheme } from '@mui/material/styles';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { useUserService } from '../../../services/users/userService';
import { userPermissionsService } from '../../../services/users/userPermissionsService';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import { MODULE_KEYS, MODULE_IDS, isFullAccessSelection } from '../../../utils/userPermissions';
import AddUserForm from './AddUserForm';
import { 
  EasyCheckButton, 
  EasyCheckDataGridBasic,
  EasyCheckPageContainer,
  EasyCheckPageHeader,
  EasyCheckPageContent,
  EasyCheckChip,
  EasyCheckIconButton
} from '../../common/Controls';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.2, 1, 0.2, 1),
  height: 'calc(100vh - 100px)',
  maxHeight: 'calc(100vh - 100px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(3, 0),
  flexShrink: 0,
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
}));

const UserManagementPage = () => {
  const theme = useTheme();
  const { user: currentUser } = useEasyCheck();
  const { snackbar, hideSnackbar, showSuccess, showError } = useSnackbar();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showHeaderFilters, setShowHeaderFilters] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [editRoleValue, setEditRoleValue] = useState('');
  const [editRoleLoading, setEditRoleLoading] = useState(false);
  const [permissionsEnabled, setPermissionsEnabled] = useState(false);
  const [editPermissions, setEditPermissions] = useState({});

  const userService = useUserService();

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'Admin';

  const hasActiveFilters = useMemo(() => {
    return filterModel.items && filterModel.items.length > 0;
  }, [filterModel.items?.length]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      // Load all users for client-side filtering like other pages
      const result = await userService.getCompanyUsers(1, 10000);
      
      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        const usersData = Array.isArray(data) ? data.map(user => ({
          id: user.Id || user.id,
          firstName: user.FirstName || user.firstName,
          lastName: user.LastName || user.lastName,
          username: user.Username || user.username,
          phoneNumber: user.PhoneNumber || user.phoneNumber,
          role: user.Role || user.role,
          modulePermissions: (() => {
            const raw = user.ModulePermissions || user.modulePermissions;
            if (!raw) return null;
            try {
              return typeof raw === 'string' ? JSON.parse(raw) : raw;
            } catch (e) {
              return null;
            }
          })(),
          isActive: user.IsActive !== undefined ? user.IsActive : user.isActive,
          emailVerified: user.EmailVerified !== undefined ? user.EmailVerified : user.emailVerified,
          createdDate: user.CreatedDate || user.createdDate,
          lastLoginDate: user.LastLoginDate || user.lastLoginDate,
          companyId: user.CompanyId || user.companyId
        })) : [];
        
        setUsers(usersData);
        setRowCount(usersData.length);
      } else {
        showError('Failed to load users');
        setUsers([]);
        setRowCount(0);
      }
    } catch (error) {
      showError('Failed to load users');
      setUsers([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [userService, showError]);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [loadUsers, isAdmin]);

  // Per-user module permissions UI only shows when the company preference is on
  useEffect(() => {
    if (isAdmin) {
      companyPreferencesService.getPreferences().then((result) => {
        if (result.success) setPermissionsEnabled(result.data.enableUserPermissions === true);
      });
    }
  }, [isAdmin]);

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleDeactivateUser = async () => {
    try {
      const result = await userService.deactivateUser(selectedUser.id);
      if (result.success) {
        showSuccess('User deactivated successfully');
        loadUsers();
      } else {
        showError('Failed to deactivate user');
      }
    } catch (error) {
      showError('Failed to deactivate user');
    }
    handleMenuClose();
  };

  const handleReactivateUser = async () => {
    try {
      const result = await userService.reactivateUser(selectedUser.id);
      if (result.success) {
        showSuccess('User reactivated successfully');
        loadUsers();
      } else {
        showError('Failed to reactivate user');
      }
    } catch (error) {
      showError('Failed to reactivate user');
    }
    handleMenuClose();
  };

  const handleResendVerification = async () => {
    try {
      const result = await userService.resendVerificationEmail(selectedUser.id);
      if (result.success) {
        showSuccess('Verification email sent successfully');
      } else {
        showError('Failed to send verification email');
      }
    } catch (error) {
      showError('Failed to send verification email');
    }
    handleMenuClose();
  };

  const handleOpenEditRole = () => {
    setEditRoleValue(selectedUser?.role || 'User');
    // null/absent map = full access -> all boxes checked
    const current = selectedUser?.modulePermissions;
    const selection = {};
    MODULE_IDS.forEach((id) => {
      selection[id] = current ? current[id] === true : true;
    });
    setEditPermissions(selection);
    setEditRoleOpen(true);
    setAnchorEl(null);
  };

  const handleCloseEditRole = () => {
    setEditRoleOpen(false);
    setSelectedUser(null);
  };

  // "ALL" = full access (stored as null so future modules stay available)
  const toAllowedKey = (map) => (map ? MODULE_IDS.filter((id) => map[id] === true).join(',') : 'ALL');

  const handleSaveRole = async () => {
    if (!selectedUser) {
      handleCloseEditRole();
      return;
    }
    const roleChanged = editRoleValue !== selectedUser.role;
    const newPermissions = isFullAccessSelection(editPermissions) ? null : editPermissions;
    const permissionsChanged = permissionsEnabled && (
      editRoleValue === 'User'
        ? toAllowedKey(newPermissions) !== toAllowedKey(selectedUser.modulePermissions)
        : selectedUser.modulePermissions != null // promoting to Admin clears any restriction
    );

    if (!roleChanged && !permissionsChanged) {
      handleCloseEditRole();
      return;
    }
    try {
      setEditRoleLoading(true);
      if (roleChanged) {
        const result = await userService.updateUserRole(selectedUser.id, editRoleValue);
        if (!result.success) {
          showError(result.message || 'Failed to update role');
          return;
        }
      }
      if (permissionsChanged) {
        const permsToSave = editRoleValue === 'User' ? newPermissions : null;
        const permResult = await userService.updateUserPermissions(selectedUser.id, permsToSave);
        if (!permResult.success) {
          showError(permResult.message || 'Failed to update module access');
          return;
        }
        userPermissionsService.clearCache();
      }
      showSuccess('User access updated successfully');
      loadUsers();
    } catch (error) {
      showError('Failed to update user');
    } finally {
      setEditRoleLoading(false);
      handleCloseEditRole();
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getStatusChip = (user) => {
    if (!user.isActive) {
      return <EasyCheckChip label="Inactive" color="error" size="small" variant="outlined" />;
    }
    if (!user.emailVerified) {
      return <EasyCheckChip label="Pending Verification" color="warning" size="small" variant="outlined" />;
    }
    return <EasyCheckChip label="Active" color="success" size="small" variant="outlined" />;
  };

  const getRoleChip = (role) => {
    return (
      <EasyCheckChip 
        label={role} 
        color={role === 'Admin' ? 'primary' : 'secondary'} 
        size="small"
        icon={role === 'Admin' ? <AdminPanelSettings /> : <Person />}
      />
    );
  };

  // Custom toolbar following the pattern from BankSetupPage
  const CustomToolbar = () => {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'space-between', p: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <EasyCheckIconButton
            onClick={() => setShowHeaderFilters(!showHeaderFilters)}
            color={hasActiveFilters ? "primary" : "default"}
            tooltip={showHeaderFilters ? "Hide filters" : "Show filters"}
          >
            {showHeaderFilters ? <FilterOffIcon /> : <FilterIcon />}
          </EasyCheckIconButton>
        </Box>
      </GridToolbarContainer>
    );
  };

  const columns = [
    {
      field: 'user',
      headerName: 'User',
      width: 250,
      renderCell: (params) => (
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            {getInitials(params.row.firstName, params.row.lastName)}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">
              {params.row.firstName} {params.row.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.id}
            </Typography>
          </Box>
        </Stack>
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'username',
      headerName: 'Email',
      width: 250,
      filterable: true,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 130,
      renderCell: (params) => getRoleChip(params.value),
      filterable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => getStatusChip(params.row),
      sortable: false,
      filterable: false,
    },
    {
      field: 'createdDate',
      headerName: 'Joined',
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString(),
      filterable: false,
    },
    {
      field: 'lastLoginDate',
      headerName: 'Last Login',
      width: 120,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'Never',
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      renderCell: (params) => (
        <Tooltip title="User actions">
          <EasyCheckIconButton
            onClick={(e) => handleMenuOpen(e, params.row)}
          >
            <MoreVert />
          </EasyCheckIconButton>
        </Tooltip>
      ),
      sortable: false,
      filterable: false,
    },
  ];

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    return users.filter(user =>
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const activeUsers = users.filter(u => u.isActive && u.emailVerified);
  const pendingUsers = users.filter(u => !u.emailVerified);

  if (!isAdmin) {
    return (
      <PageContainer>
        <Alert severity="warning">
          <Typography variant="h6" gutterBottom>Access Restricted</Typography>
          <Typography>You need Admin privileges to access user management.</Typography>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header Section */}
      <HeaderSection>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          User Management
        </Typography>
        <EasyCheckButton
          startIcon={<PersonAdd />}
          onClick={() => setAddUserOpen(true)}
        >
          Add User
        </EasyCheckButton>
      </HeaderSection>

      {/* Summary Card */}
      <SummaryCard>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Company: {currentUser?.companyName}
          </Typography>
          <Stack direction="row" spacing={3}>
            <Box>
              <Typography variant="h4" color="primary.main">
                {rowCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="h4" color="success.main">
                {activeUsers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="h4" color="warning.main">
                {pendingUsers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Verification
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </SummaryCard>

      {/* Search Field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Data Grid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <EasyCheckDataGridBasic
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={filteredUsers.length}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          slots={{
            toolbar: CustomToolbar,
            columnMenu: GridColumnMenu,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
            },
            columnMenu: {
              showQuickFilter: false,
            },
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #f5f5f5',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleOpenEditRole}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Role</ListItemText>
        </MenuItem>
        
        {!selectedUser?.emailVerified && (
          <MenuItem onClick={handleResendVerification}>
            <ListItemIcon>
              <Email fontSize="small" />
            </ListItemIcon>
            <ListItemText>Resend Verification</ListItemText>
          </MenuItem>
        )}

        <Divider />

        {selectedUser?.isActive ? (
          <MenuItem onClick={handleDeactivateUser} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <PersonOff fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Deactivate User</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleReactivateUser} sx={{ color: 'success.main' }}>
            <ListItemIcon>
              <PersonOutline fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Reactivate User</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Edit Role & Access Dialog */}
      <Dialog open={editRoleOpen} onClose={handleCloseEditRole} maxWidth="xs" fullWidth>
        <DialogTitle>{permissionsEnabled ? 'Edit Role & Access' : 'Edit Role'}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Change role for <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.username})
              </Typography>
              <FormControl>
                <RadioGroup
                  value={editRoleValue}
                  onChange={(e) => setEditRoleValue(e.target.value)}
                >
                  <FormControlLabel
                    value="User"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">User</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Can manage cheques, beneficiaries, and view reports
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="Admin"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Admin</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Full access including user management and company settings
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
              {permissionsEnabled && editRoleValue === 'User' && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Module Access
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    All modules checked = full access (includes future modules)
                  </Typography>
                  <FormGroup>
                    {MODULE_KEYS.map((module) => (
                      <FormControlLabel
                        key={module.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={editPermissions[module.id] === true}
                            onChange={(e) =>
                              setEditPermissions((prev) => ({ ...prev, [module.id]: e.target.checked }))
                            }
                          />
                        }
                        label={
                          <Tooltip title={module.description} placement="right">
                            <Typography variant="body2">{module.label}</Typography>
                          </Tooltip>
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <EasyCheckButton variant="outlined" onClick={handleCloseEditRole} disabled={editRoleLoading}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton onClick={handleSaveRole} disabled={editRoleLoading}>
            {editRoleLoading ? 'Saving...' : 'Save'}
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <AddUserForm 
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onSuccess={loadUsers}
      />

      {/* Snackbar Notification */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transition="slide"
        showIcon={true}
      />
    </PageContainer>
  );
};

export default UserManagementPage; 